import { c as _c } from "react/compiler-runtime";
import { DndContext, DragEndEvent, DragOverlay, DropAnimation, MeasuringStrategy, MouseSensor, TouchSensor, UniqueIdentifier, closestCenter, defaultDropAnimationSideEffects, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { changeFunnelStepPosition } from 'modules/funnels/funnel/api/funnel-api';
import CreateFunnelStepModal from 'modules/funnels/funnel/components/create-funnel-step-modal';
import DraggableFunnelStep from 'modules/funnels/funnel/components/draggable-funnel-step';
import InactivePage from 'modules/funnels/funnel/components/inactive-page';
import OverlayFunnelStep from 'modules/funnels/funnel/components/overlay-funnel-step';
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface';
import { FunnelId } from 'modules/funnels/types/funnel-interface';
import React, { useState } from 'react';
import { KeyedMutator } from 'swr';
interface FunnelStepsListProps {
  funnelSteps: FunnelStepInterface[];
  mutate: KeyedMutator<FunnelStepInterface[]>;
  selectedFunnelStep: FunnelStepInterface | null;
  selectFunnelStep: (funnelId: FunnelStepInterface) => void;
  funnelId: FunnelId;
  listRef: React.RefObject<HTMLUListElement | null>;
  listHeight: string | number;
}
const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4'
      }
    }
  })
};
function FunnelStepsList(t0) {
  const $ = _c(38);
  const {
    funnelSteps,
    mutate,
    selectedFunnelStep,
    selectFunnelStep,
    funnelId,
    listRef,
    listHeight
  } = t0;
  const [draggableId, setDraggableId] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      activationConstraint: {
        distance: 10
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      activationConstraint: {
        delay: 100,
        tolerance: 5
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sensors = useSensors(useSensor(MouseSensor, t1), useSensor(TouchSensor, t2));
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async () => {
      setDraggableId(null);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const onDragCancel = t3;
  let t4;
  if ($[3] !== funnelSteps || $[4] !== mutate) {
    t4 = async t5 => {
      const {
        active,
        over
      } = t5;
      if (over && active.id !== over?.id) {
        const activeIndex = funnelSteps.findIndex(t6 => {
          const {
            id
          } = t6;
          return id === active.id;
        });
        const overIndex = funnelSteps.findIndex(t7 => {
          const {
            id: id_0
          } = t7;
          return id_0 === over.id;
        });
        const activeItem = funnelSteps.find(t8 => {
          const {
            id: id_1
          } = t8;
          return id_1 === active.id;
        });
        const overItem = funnelSteps.find(t9 => {
          const {
            id: id_2
          } = t9;
          return id_2 === over.id;
        });
        if (activeItem && overItem && activeIndex !== overIndex) {
          const clonedSteps = [...funnelSteps];
          await mutate(data => {
            if (data) {
              return arrayMove(funnelSteps, activeIndex, overIndex);
            }
          }, false);
          ;
          try {
            await changeFunnelStepPosition(Number(active.id), {
              newPosition: overIndex + 1
            });
          } catch (t10) {
            await mutate(data_0 => {
              if (data_0) {
                return {
                  ...data_0,
                  funnelSteps: clonedSteps
                };
              }
            }, false);
          }
        }
      }
      setDraggableId(null);
    };
    $[3] = funnelSteps;
    $[4] = mutate;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onDragEnd = t4;
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      droppable: {
        strategy: MeasuringStrategy.Always
      }
    };
    t6 = t7 => {
      const {
        active: active_0
      } = t7;
      setDraggableId(active_0.id);
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  const t7 = listRef as React.LegacyRef<HTMLUListElement>;
  let t8;
  if ($[8] !== listHeight) {
    t8 = listHeight ? {
      height: listHeight,
      minHeight: "calc(100vh - 15rem)"
    } : {};
    $[8] = listHeight;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== funnelSteps || $[11] !== selectFunnelStep || $[12] !== selectedFunnelStep) {
    let t10;
    if ($[14] !== selectFunnelStep || $[15] !== selectedFunnelStep) {
      t10 = funnel => <DraggableFunnelStep key={funnel.id} funnelStep={funnel} selectedFunnelStep={selectedFunnelStep} selectFunnelStep={selectFunnelStep} />;
      $[14] = selectFunnelStep;
      $[15] = selectedFunnelStep;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    t9 = funnelSteps.map(t10);
    $[10] = funnelSteps;
    $[11] = selectFunnelStep;
    $[12] = selectedFunnelStep;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[17] !== funnelId) {
    t10 = <InactivePage funnelId={funnelId} />;
    $[17] = funnelId;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t11 = <ul ref={t7} className="funnelCustomScrollContainer customScroll text-text-primary inset-0 flex h-[calc(46vh)] w-full flex-col overflow-y-auto rounded-md text-base last:border-b-transparent lg:h-[calc(100vh-15rem)] lg:w-[330px] lg:overflow-y-hidden lg:hover:overflow-y-auto" style={t8}>{t9}{t10}</ul>;
    $[19] = t10;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <CreateFunnelStepModal />;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11) {
    t13 = <div className="bg-background-secondary flex flex-col">{t11}{t12}</div>;
    $[25] = t11;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== funnelSteps || $[28] !== t13) {
    t14 = <SortableContext items={funnelSteps} strategy={verticalListSortingStrategy}>{t13}</SortableContext>;
    $[27] = funnelSteps;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== draggableId || $[31] !== funnelSteps) {
    t15 = draggableId && <DragOverlay dropAnimation={dropAnimationConfig}><OverlayFunnelStep funnelStep={funnelSteps.find(item => item.id === draggableId)} /></DragOverlay>;
    $[30] = draggableId;
    $[31] = funnelSteps;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== onDragEnd || $[34] !== sensors || $[35] !== t14 || $[36] !== t15) {
    t16 = <DndContext sensors={sensors} collisionDetection={closestCenter} measuring={t5} onDragStart={t6} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>{t14}{t15}</DndContext>;
    $[33] = onDragEnd;
    $[34] = sensors;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
}
export default FunnelStepsList;